import React from 'react';
import { FaTimes, FaChartLine, FaUsers, FaCrown, FaCalendar, FaCheck, FaTimes as FaTimesIcon } from 'react-icons/fa';
import { useMarketingObjectives } from '../../hooks/useMarketingObjectives';

const ObjectiveHistoryModal = ({ isOpen, onClose, userId }) => {
    const { objectivesHistory, loading, error } = useMarketingObjectives(userId);

    const getIcon = (objectiveType) => {
        switch (objectiveType) {
            case 'INCREASE_SALES':
                return <FaChartLine className="text-green-500" />;
            case 'ACQUIRE_NEW_CUSTOMERS':
                return <FaUsers className="text-blue-500" />;
            case 'RETAIN_EXISTING_CUSTOMERS':
                return <FaCrown className="text-purple-500" />;
            default:
                return <FaChartLine className="text-gray-500" />;
        }
    };

    const getStatusIcon = (isActive) => {
        return isActive ? (
            <FaCheck className="text-green-500" />
        ) : (
            <FaTimesIcon className="text-gray-400" />
        );
    };

    const formatDate = (dateString) => {
        return new Date(dateString).toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'short',
            day: 'numeric'
        });
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <div className="bg-white rounded-lg shadow-xl max-w-4xl w-full mx-4 max-h-[90vh] overflow-y-auto">
                {/* Header */}
                <div className="flex items-center justify-between p-6 border-b">
                    <h2 className="text-2xl font-bold text-gray-900">
                        Historial de Objetivos
                    </h2>
                    <button
                        onClick={onClose}
                        className="text-gray-400 hover:text-gray-600 transition-colors"
                    >
                        <FaTimes className="text-xl" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-6">
                    {loading ? (
                        <div className="flex items-center justify-center py-12">
                            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-500"></div>
                            <span className="ml-3 text-lg">Cargando historial...</span>
                        </div>
                    ) : error ? (
                        <div className="text-center py-12 text-red-500">
                            <p className="text-lg">Error al cargar el historial</p>
                            <p className="text-sm mt-2">{error}</p>
                        </div>
                    ) : objectivesHistory.length === 0 ? (
                        <div className="text-center py-12 text-gray-500">
                            <FaCalendar className="text-6xl mx-auto mb-4 text-gray-300" />
                            <h3 className="text-xl font-semibold mb-2">No hay historial de objetivos</h3>
                            <p>Los objetivos que establezcas aparecerán aquí</p>
                        </div>
                    ) : (
                        <div className="space-y-6">
                            {/* Objetivo Actual */}
                            {objectivesHistory.length > 0 && objectivesHistory[0].isActive && (
                                <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                                    <div className="flex items-center justify-between mb-3">
                                        <h3 className="text-lg font-semibold text-blue-900">
                                            Objetivo Actual
                                        </h3>
                                        <div className="flex items-center">
                                            <FaCheck className="text-green-500 mr-2" />
                                            <span className="text-sm text-green-700 font-medium">Activo</span>
                                        </div>
                                    </div>
                                    <div className="flex items-start space-x-4">
                                        <div className="text-2xl mt-1">
                                            {getIcon(objectivesHistory[0].objectiveType)}
                                        </div>
                                        <div className="flex-1">
                                            <h4 className="font-semibold text-gray-900 text-lg">
                                                {objectivesHistory[0].objectiveName}
                                            </h4>
                                            <p className="text-gray-600 mt-2">
                                                {objectivesHistory[0].objectiveDescription}
                                            </p>
                                            <div className="flex items-center mt-3 text-sm text-gray-500">
                                                <FaCalendar className="mr-2" />
                                                Establecido el {formatDate(objectivesHistory[0].startDate)}
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Historial de Objetivos */}
                            <div>
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">
                                    Objetivos Anteriores
                                </h3>
                                <div className="space-y-4">
                                    {objectivesHistory
                                        .filter(obj => !obj.isActive)
                                        .map((objective, index) => (
                                            <div key={objective._id || index} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors">
                                                <div className="flex items-start space-x-4">
                                                    <div className="text-xl mt-1">
                                                        {getIcon(objective.objectiveType)}
                                                    </div>
                                                    <div className="flex-1">
                                                        <div className="flex items-center justify-between mb-2">
                                                            <h4 className="font-semibold text-gray-900">
                                                                {objective.objectiveName}
                                                            </h4>
                                                            <div className="flex items-center">
                                                                <FaTimesIcon className="text-gray-400 mr-2" />
                                                                <span className="text-sm text-gray-500">Inactivo</span>
                                                            </div>
                                                        </div>
                                                        <p className="text-gray-600 text-sm mb-3">
                                                            {objective.objectiveDescription}
                                                        </p>
                                                        <div className="flex items-center text-xs text-gray-500">
                                                            <FaCalendar className="mr-2" />
                                                            <span>
                                                                {formatDate(objective.startDate)}
                                                                {objective.endDate && (
                                                                    <> - Finalizado el {formatDate(objective.endDate)}</>
                                                                )}
                                                            </span>
                                                        </div>
                                                    </div>
                                                </div>
                                            </div>
                                        ))}
                                </div>
                            </div>

                            {/* Estadísticas */}
                            <div className="bg-gray-50 rounded-lg p-4 mt-6">
                                <h4 className="font-semibold text-gray-900 mb-3">Resumen</h4>
                                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 text-sm">
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-blue-600">
                                            {objectivesHistory.length}
                                        </div>
                                        <div className="text-gray-600">Total de objetivos</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-green-600">
                                            {objectivesHistory.filter(obj => obj.isActive).length}
                                        </div>
                                        <div className="text-gray-600">Objetivos activos</div>
                                    </div>
                                    <div className="text-center">
                                        <div className="text-2xl font-bold text-gray-600">
                                            {objectivesHistory.filter(obj => !obj.isActive).length}
                                        </div>
                                        <div className="text-gray-600">Objetivos completados</div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="flex justify-end p-6 border-t">
                    <button
                        onClick={onClose}
                        className="px-6 py-2 bg-gray-100 text-gray-700 rounded-md hover:bg-gray-200 transition-colors"
                    >
                        Cerrar
                    </button>
                </div>
            </div>
        </div>
    );
};

export default ObjectiveHistoryModal; 